import { useCallback, useMemo } from 'react'
import { useResizeDetector } from 'react-resize-detector'

import { TimesResult, Hours } from 'interface/kojin/log'
import { WeekDays } from 'shared/weekDay'

import ReactEChartsCore from 'echarts-for-react/lib/core'
import * as echarts from 'echarts/core'
import { HeatmapChart } from 'echarts/charts'
import { CanvasRenderer } from 'echarts/renderers'

import { Box } from "@mui/material"

import {
  GridComponent,
  VisualMapComponent
} from 'echarts/components'

echarts.use([
  GridComponent,
  HeatmapChart,
  VisualMapComponent,
  CanvasRenderer
])

const gradientColor = ['#FE8', '#a50026']
const selectedStyle = {
  color: 'rgb(84, 112, 198)'
}
const emphasisStyle = {
  itemStyle: {
    shadowBlur: 10,
    shadowColor: 'rgba(0, 0, 0, 0.5)'
  }
}

type AccessTimeHeatMapProps = {
  list: TimesResult[]
	currentWeekDay: string[]
	currentHour: number[]
  setCurrent: (weekDay: string[], hour: number[]) => void
  height: number
}
export const AccessTimeHeatMap = ({
  list,
  currentWeekDay,
  currentHour,
  setCurrent,
  height
}: AccessTimeHeatMapProps) => {
  const { width, ref } = useResizeDetector()
  const max = useMemo(() => list.reduce((s, r) => Math.max(r.count, s), 0), [list])

  const {
    data,
    hours,
    hoursData,
    weekDays,
    weekDaysData
   } = useMemo(
    () => {
      const data = list.map(r => {
        const weekDayIndex = r.weekDay === 1 ? 6 : (r.weekDay - 2)
        const weekDay = Object.keys(WeekDays)[weekDayIndex]
        const isCurrent = (
          currentHour.length > 0 && currentHour.includes(r.hour) &&
          currentWeekDay.length > 0 && currentWeekDay.includes(weekDay)
        )
        return {
          value: [r.hour, weekDayIndex, r.count],
          itemStyle: isCurrent ? selectedStyle : undefined
        }
      })
      const hours = Hours
      const hoursData = hours.flatMap((_, hour) => {
        const count = data.filter(d => d.value[0] === hour).reduce((acc, d) => acc + d.value[2], 0)
        if (count === 0) return []
        return {
          value: [hour, 0, count],
          itemStyle: (currentWeekDay.length === 0 && currentHour.includes(hour)) ? selectedStyle : undefined
        }
      })
      const weekDays = Object.values(WeekDays).map(w => w.short)
      const weekDaysData = Object.keys(WeekDays).flatMap((weekDay, weekDayIndex) => {
        const count = data.filter(d => d.value[1] === weekDayIndex).reduce((acc, d) => acc + d.value[2], 0)
        if (count === 0) return []
        return {
          value: [0, weekDayIndex, count],
          itemStyle: (currentHour.length === 0 && currentWeekDay?.includes(weekDay)) ? selectedStyle : undefined
        }
      })
      return {
        data,
        hours,
        hoursData,
        weekDays,
        weekDaysData
      }
    },
    [list, currentHour, currentWeekDay]
  )
  
  const grid = useMemo(
    () => {
      const padding = 8
      const margin = 5
      const sz = {
        cx: (width ?? 0) - (padding + padding),
        cy: height - (padding + padding)
      }
      const rowHeight = sz.cy / 9
      const colWidth = sz.cx / 15
      return [{
        top: padding + rowHeight + margin,
        bottom: padding,
        left: padding,
        right: margin + colWidth + padding
      }, {
        top: padding,
        bottom: (sz.cy - rowHeight) + padding,
        left: padding,
        right: margin + colWidth + padding
      }, {
        top: padding + rowHeight + margin,
        bottom: padding,
        left: (sz.cx - colWidth) + padding,
        right: padding
      }]
    },
    [width, height]
  )
  const xAxis = useMemo(
    () => [0, 1, 2].map(index => ({
      type: 'category',
      data: index === 2 ? ['計'] : hours,
      splitArea: { show: true },
      axisTick: { show: false },
      axisLabel: { show: index === 1 ? false : true },
      axisLine: { show: index === 1 ? false : true },
      gridIndex: index
    })),
    [hours]
  )
  const yAxis = useMemo(
    () => [0, 1, 2].map(index => ({
      type: 'category',
      data: index === 1 ? ['計'] : weekDays,
      inverse: index === 1 ? undefined : weekDays,
      splitArea: { show: true },
      axisTick: { show: false },
      axisLabel: { show: index === 2 ? false : true },
      axisLine: { show: index === 2 ? false : true },
      gridIndex: index
    })),
    [weekDays]
  )

  const onClickGraph = useCallback(
		(params?: {
      seriesIndex?: number
			data: { value: number[] }
		}) => {
      if (!params) return

      const indexes = params.data.value
			if (!indexes || indexes.length < 3) return

      const [hour, weekDayIndex] = indexes
      const weekDay = Object.keys(WeekDays)[weekDayIndex]
      if (params.seriesIndex === 0) {
        const isClear = currentHour.includes(hour) && currentWeekDay.includes(weekDay)
        setCurrent(isClear ? [] : [weekDay], isClear ? [] : [hour])
      } else if (params.seriesIndex === 1) {
        setCurrent([], (currentWeekDay.length === 0 && currentHour.includes(hour)) ? [] : [hour])
      } else if (params.seriesIndex === 2) {
        setCurrent((currentHour.length === 0 && currentWeekDay.includes(weekDay)) ? [] : [weekDay], [])
      }
		},
		[setCurrent, currentHour, currentWeekDay]
	)
  return (
    <Box ref={ref}>
      <ReactEChartsCore
        echarts={echarts}
        notMerge={true}
        onEvents={{
          click: onClickGraph
        }}
        option={{
          gradientColor,
          visualMap: {
            show: false,
            min: 0, max: max
          },
          grid, xAxis, yAxis,
          series: [{
            name: 'times',
            type: 'heatmap',
            data, xAxisIndex: 0, yAxisIndex: 0,
            label: { show: true },
            emphasis: emphasisStyle
          }, {
            name: 'hours',
            type: 'heatmap',
            data: hoursData, xAxisIndex: 1, yAxisIndex: 1,
            label: { show: true },
            emphasis: emphasisStyle
          }, {
            name: 'weekdays',
            type: 'heatmap',
            data: weekDaysData, xAxisIndex: 2, yAxisIndex: 2,
            label: { show: true },
            emphasis: emphasisStyle
          }]
        }}
        opts={{ renderer: 'canvas' }}
        style={{ width, height }}
        theme="insite"
      />
    </Box>
  )
}
